import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Alert,
  Chip,
  Box,
  Autocomplete,
  InputAdornment,
  Divider,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { GRNItem } from "../../types/grn.types";
import { grnService } from "../../services/grn.service";
import { useAuth } from "../../../auth/hooks/useAuth";

interface IngredientConfigDialogProps {
  open: boolean;
  onClose: () => void;
  item: GRNItem | null;
  onSave: (updatedItem: GRNItem) => void;
}

// Common ingredient categories
const INGREDIENT_CATEGORIES = [
  "Grains & Cereals",
  "Proteins",
  "Dairy & Eggs",
  "Vegetables",
  "Fruits",
  "Herbs & Spices",
  "Oils & Fats",
  "Condiments",
  "Beverages",
  "Frozen Items",
  "Canned Goods",
  "Baking Supplies",
  "Other",
];

// Common allergens
const COMMON_ALLERGENS = [
  "Gluten",
  "Dairy",
  "Eggs",
  "Nuts",
  "Peanuts",
  "Soy",
  "Fish",
  "Shellfish",
  "Sesame",
];

// Common units
const COMMON_UNITS = [
  "kg",
  "g",
  "lb",
  "oz",
  "L",
  "ml",
  "cups",
  "pieces",
  "boxes",
  "cans",
];

export const IngredientConfigDialog: React.FC<IngredientConfigDialogProps> = ({
  open,
  onClose,
  item,
  onSave,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<GRNItem>>({});
  const [checking, setChecking] = useState(false);
  const [ingredientExists, setIngredientExists] = useState<boolean | null>(
    null
  );
  const [existingIngredient, setExistingIngredient] = useState<any>(null);

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        ...item,
        create_as_ingredient: item.create_as_ingredient || false,
        category: item.category || "",
        base_unit: item.base_unit || item.unit,
        conversion_factor: item.conversion_factor || 1,
        min_stock_level: item.min_stock_level || 0,
        max_stock_level: item.max_stock_level || item.received_quantity * 2,
        storage_instructions: item.storage_instructions || "",
        allergens: item.allergens || [],
        brand_name: item.brand_name || "",
        inspection_notes: item.inspection_notes || "",
        description: item.description || `${item.item_name} - From supplier`,
      });

      // Check if ingredient exists when dialog opens
      if (item.item_name && item.create_as_ingredient) {
        checkIngredientExists(item.item_name);
      }
    }
  }, [item]);

  const checkIngredientExists = async (name: string) => {
    if (!user?.business_id || !name.trim()) return;

    setChecking(true);
    try {
      const response = await grnService.checkIngredientExists({
        name: name.trim(),
        business_id: user.business_id,
      });

      if (response.success) {
        setIngredientExists(response.data.exists);
        setExistingIngredient(response.data.ingredient || null);

        setFormData((prev) => ({
          ...prev,
          ingredient_exists: response.data.exists,
          existing_ingredient_id: response.data.ingredient?._id || "",
        }));
      }
    } catch (error: any) {
      console.error("Error checking ingredient existence:", error);
      setIngredientExists(null);
      setExistingIngredient(null);
    } finally {
      setChecking(false);
    }
  };

  const handleFieldChange = (field: keyof GRNItem, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Check ingredient existence when name changes
    if (field === "item_name" && value && formData.create_as_ingredient) {
      checkIngredientExists(value);
    }
  };

  const handleCreateAsIngredientToggle = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, create_as_ingredient: checked }));

    if (checked && formData.item_name) {
      checkIngredientExists(formData.item_name);
    } else {
      setIngredientExists(null);
      setExistingIngredient(null);
    }
  };

  const handleSave = () => {
    if (!item) return;

    const updatedItem: GRNItem = {
      ...item,
      ...formData,
    };

    onSave(updatedItem);
    onClose();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: "90vh" } }}
    >
      <DialogTitle>
        <Box>
          <Typography variant="h6">Configure Ingredient Creation</Typography>
          <Typography variant="body2" color="textSecondary">
            {item.item_name} • {item.received_quantity} {item.unit} •{" "}
            {formatCurrency(item.total_cost)}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pb: 2 }}>
        {/* Create as Ingredient Toggle */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.create_as_ingredient || false}
                onChange={(e) =>
                  handleCreateAsIngredientToggle(e.target.checked)
                }
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="subtitle2">
                  Create as Ingredient
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  When enabled, this item will be added to your ingredients
                  inventory with batch tracking
                </Typography>
              </Box>
            }
          />
        </Box>

        {formData.create_as_ingredient && (
          <>
            {/* Ingredient Existence Check */}
            {checking && (
              <Alert
                severity="info"
                icon={<CircularProgress size={16} />}
                sx={{ mb: 2 }}
              >
                Checking if ingredient already exists...
              </Alert>
            )}

            {ingredientExists === true && existingIngredient && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Ingredient Already Exists
                </Typography>
                <Typography variant="body2">
                  "{existingIngredient.name}" (SKU: {existingIngredient.sku})
                  already exists with {existingIngredient.current_stock}{" "}
                  {existingIngredient.unit} in stock. A new batch will be added
                  to the existing ingredient.
                </Typography>
              </Alert>
            )}

            {ingredientExists === false && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">New Ingredient</Typography>
                <Typography variant="body2">
                  This will create a new ingredient in your inventory system.
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Basic Information */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Basic Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Item Name"
                  value={formData.item_name || ""}
                  onChange={(e) =>
                    handleFieldChange("item_name", e.target.value)
                  }
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={INGREDIENT_CATEGORIES}
                  value={formData.category || ""}
                  onChange={(_, value) =>
                    handleFieldChange("category", value || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      placeholder="Select or type category"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description || ""}
                  onChange={(e) =>
                    handleFieldChange("description", e.target.value)
                  }
                  multiline
                  rows={2}
                  placeholder="Brief description of the ingredient"
                />
              </Grid>
            </Grid>

            {/* Units & Conversion */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              Units & Conversion
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Primary Unit"
                  value={formData.unit || ""}
                  disabled
                  helperText="From PO item"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={COMMON_UNITS}
                  value={formData.base_unit || ""}
                  onChange={(_, value) =>
                    handleFieldChange("base_unit", value || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Base Unit"
                      placeholder="e.g., kg, L, pieces"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Conversion Factor"
                  type="number"
                  value={formData.conversion_factor || 1}
                  onChange={(e) =>
                    handleFieldChange(
                      "conversion_factor",
                      parseFloat(e.target.value) || 1
                    )
                  }
                  inputProps={{ min: 0.001, step: 0.001 }}
                  helperText="1 primary unit = X base units"
                />
              </Grid>
            </Grid>

            {/* Stock Levels */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              Stock Management
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Minimum Stock Level"
                  type="number"
                  value={formData.min_stock_level || 0}
                  onChange={(e) =>
                    handleFieldChange(
                      "min_stock_level",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  inputProps={{ min: 0 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.base_unit || formData.unit}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Maximum Stock Level"
                  type="number"
                  value={formData.max_stock_level || 0}
                  onChange={(e) =>
                    handleFieldChange(
                      "max_stock_level",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  inputProps={{ min: 0 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.base_unit || formData.unit}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Batch Information */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              Batch Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Brand Name"
                  value={formData.brand_name || ""}
                  onChange={(e) =>
                    handleFieldChange("brand_name", e.target.value)
                  }
                  placeholder="Manufacturer or brand"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Batch Number"
                  value={formData.batch_number || ""}
                  onChange={(e) =>
                    handleFieldChange("batch_number", e.target.value)
                  }
                  placeholder="Leave empty for auto-generation"
                />
              </Grid>

              {formData.is_expiry_tracked && (
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Expiry Date"
                    value={
                      formData.expiry_date ? dayjs(formData.expiry_date) : null
                    }
                    onChange={(date) =>
                      handleFieldChange(
                        "expiry_date",
                        date?.valueOf() || undefined
                      )
                    }
                    minDate={dayjs()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required={formData.is_expiry_tracked}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>

            {/* Allergens */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              Allergens & Safety
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={COMMON_ALLERGENS}
                  value={formData.allergens || []}
                  onChange={(_, value) => handleFieldChange("allergens", value)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Allergens"
                      placeholder="Select or type allergens"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Storage Instructions"
                  value={formData.storage_instructions || ""}
                  onChange={(e) =>
                    handleFieldChange("storage_instructions", e.target.value)
                  }
                  multiline
                  rows={2}
                  placeholder="e.g., Store in cool, dry place"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Inspection Notes"
                  value={formData.inspection_notes || ""}
                  onChange={(e) =>
                    handleFieldChange("inspection_notes", e.target.value)
                  }
                  multiline
                  rows={2}
                  placeholder="Quality inspection notes"
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          disabled={formData.create_as_ingredient && !formData.category}
        >
          Save Configuration
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
